import { useEffect } from 'react'
import { ReactNode } from 'react'
import { Button } from '@systemeio/components'
import { CheckIcon, Download01Icon } from '@systemeio/icons'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next/pages'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { useDownloadCertificate } from 'modules/course/api/download-certificate'
import CourseLayout from 'modules/course/components/course-layout'
import useCertificateOfCompletion from 'modules/course/hooks/use-certificate-of-completion'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import useStepRouter from 'modules/course/hooks/use-step-router'
import { getCoursePageLayout } from 'modules/course/layouts/get-course-page-layout'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'

export default function CertificateOfCompletion(): ReactNode {
  const { t } = useTranslation()
  const { getCertificateLink, isCertificateAvailable } = useCertificateOfCompletion()
  const { courseData } = useCourseData()
  const { findStep, redirectToStep } = useStepRouter()

  const downloadCertificateMutation = useDownloadCertificate(
    `${courseData?.name || 'Certificate'}.pdf`,
  )

  useEffect(() => {
    if (!isCertificateAvailable) {
      redirectToStep(findStep())
    }
  }, [isCertificateAvailable, findStep])

  const handleDownload = (): void => {
    downloadCertificateMutation.mutate(getCertificateLink())
  }

  return (
    <CourseLayout>
      <div
        data-testid="course-certificate"
        className="flex w-full min-w-0 flex-col items-center gap-3 rounded-2xl border border-THEME-primary/10 bg-THEME-block-background px-4 pt-6 pb-9 text-center"
      >
        <div className="flex flex-col items-center gap-2">
          <div
            data-testid="course-certificate-success-icon"
            className="flex size-15 items-center justify-center text-THEME-secondary"
          >
            <div className="flex size-14 items-center justify-center rounded-full bg-current">
              <CheckIcon className="size-8 text-THEME-block-background" />
            </div>
          </div>
          <h2 className="text-4xl font-semibold wrap-break-word text-THEME-primary">
            {t('course.quiz.pass.congratulation')}
          </h2>
        </div>
        <div className="flex w-full flex-col items-center gap-4 desktop:max-w-md">
          <p
            className="text-base wrap-break-word text-THEME-primary/60"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                t('course.certificate.page.description', { courseName: courseData?.name }),
              ),
            }}
          />
          <Button
            className={`max-w-full border-THEME-secondary! bg-THEME-secondary! whitespace-normal text-THEME-block-background! hover:bg-THEME-hover-secondary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:bg-THEME-disabled! ${addPublicCustomClassNamePrefix(['course__button', 'course__button--primary'])}`}
            onClick={handleDownload}
            isLoading={downloadCertificateMutation.isPending}
            disabled={!isCertificateAvailable}
          >
            <Download01Icon className="size-5" />
            {t('course.certificate.download')}
          </Button>
        </div>
      </div>
    </CourseLayout>
  )
}

CertificateOfCompletion.getLayout = getCoursePageLayout

export const getServerSideProps = withDomainAssets(baseServerSideProps)
